import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShareNetwork, Copy, Check, LinkBreak, CircleNotch } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";

export default function ShareButton({ docId, initialToken }) {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState(initialToken || null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const url = token ? `${window.location.origin}/shared/${token}` : "";

  const enable = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/documents/${docId}/share`);
      setToken(res.data.share_token);
    } catch {
      toast.error("Could not create share link");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.delete(`/documents/${docId}/share`);
      setToken(null);
      toast.success("Share link revoked");
    } catch {
      toast.error("Could not revoke link");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed");
    }
  };

  const onOpenChange = (o) => {
    setOpen(o);
    if (o && !token) enable();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <button
          data-testid="share-button"
          className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
        >
          <ShareNetwork size={16} weight="duotone" /> Share
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-sm" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle>Share read-only link</DialogTitle>
          <DialogDescription>
            Anyone with this link can view a clean, print-friendly copy of this document — no sign-in required. They cannot edit it.
          </DialogDescription>
        </DialogHeader>

        {busy && !token ? (
          <div className="flex items-center gap-2 text-slate-500 py-4">
            <CircleNotch size={18} className="animate-spin" /> Generating link…
          </div>
        ) : token ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <input
                data-testid="share-url-input"
                readOnly
                value={url}
                className="flex-1 h-10 rounded-sm border border-slate-300 bg-slate-50 px-3 text-sm font-mono text-slate-700 focus:outline-none"
              />
              <button
                data-testid="share-copy-button"
                onClick={copy}
                className="inline-flex items-center gap-1.5 rounded-sm bg-slate-900 px-3 py-2.5 text-sm font-medium text-white hover:bg-slate-800 transition-colors"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <button
              data-testid="share-revoke-button"
              onClick={revoke}
              disabled={busy}
              className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 transition-colors"
            >
              <LinkBreak size={16} /> Revoke link
            </button>
          </div>
        ) : (
          <button
            data-testid="share-enable-button"
            onClick={enable}
            className="inline-flex items-center gap-2 rounded-sm bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
          >
            <ShareNetwork size={16} /> Create link
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
